
import { useState } from 'react';
import { database } from '../../lib/database';

export default function DataExportImport() {
  const [isExporting, setIsExporting] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStatus, setImportStatus] = useState('');

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const exportData = await database.exportAllData();
      
      // Create and download file
      const blob = new Blob([exportData], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `dbd-podcast-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export failed:', error);
      alert('Export failed. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setIsImporting(true);
      setImportStatus('');
      
      const text = await file.text();
      await database.importAllData(text);
      
      setImportStatus('success');
      // Reload page to reflect changes
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error('Import failed:', error);
      setImportStatus('error');
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Data Export & Import</h2>
        <p className="text-gray-600">Backup your data or restore from a previous backup</p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {/* Export Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Export Data</h3>
          <p className="text-gray-600 mb-6">
            Download all episodes, categories, and industries as a JSON file for backup or migration.
          </p>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isExporting ? (
              <span className="flex items-center justify-center">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Exporting...
              </span>
            ) : (
              <span className="flex items-center justify-center">
                <i className="ri-download-line mr-2"></i>
                Export All Data
              </span>
            )}
          </button>
        </div>

        {/* Import Section */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Import Data</h3>
          <p className="text-gray-600 mb-6">
            Restore data from a previously exported JSON file. This will replace all current data.
          </p>
          
          <div className="mb-4">
            <input
              type="file"
              accept=".json"
              onChange={handleImport}
              disabled={isImporting}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 cursor-pointer"
            />
          </div>

          {isImporting && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
              <i className="ri-loader-4-line animate-spin text-blue-500 text-xl mb-2"></i>
              <p className="text-blue-700 font-medium">Importing data...</p>
            </div>
          )}

          {importStatus === 'success' && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
              <i className="ri-check-circle-line text-green-500 text-xl mb-2"></i>
              <p className="text-green-700 font-medium">Import successful! Page will reload shortly.</p>
            </div>
          )}

          {importStatus === 'error' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <i className="ri-error-warning-line text-red-500 text-xl mb-2"></i>
              <p className="text-red-700 font-medium">Import failed. Please check the file format.</p>
            </div>
          )}
        </div>
      </div>

      {/* Migration Instructions */}
      <div className="mt-8 bg-gray-50 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Migration to Self-Hosted Postgres</h3>
        <div className="space-y-4 text-sm text-gray-700">
          <div>
            <h4 className="font-semibold mb-2">Step 1: Export your data</h4>
            <p>Use the export function above to download all your data as JSON.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Step 2: Set up Postgres database</h4>
            <p>Create a new Postgres database and run the schema from <code className="bg-gray-200 px-2 py-1 rounded">src/lib/supabase.ts</code></p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Step 3: Update configuration</h4>
            <p>Update your database connection settings to point to your self-hosted Postgres instance.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-2">Step 4: Import data</h4>
            <p>Use the import function to restore your data in the new database.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
